<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import L from 'leaflet'
import { useRouterStore } from '@/store/router'
import { useEstudios } from '@/composables/useEstudios'
import { useEstudioDashboardTokens } from '@/composables/useEstudioDashboardTokens'
import { useNominatim } from '@/composables/useNominatim'
import { useMapTiles } from '@/composables/useMapTiles'
import { useGoogleMapsUrl } from '@/composables/useGoogleMapsUrl'
import { DEFAULT_CENTER } from '@/store/mapConstants'
import { usePublicTheme } from '@/composables/usePublicTheme'

const themeStore = usePublicTheme()
const route = useRouterStore()
const token = computed(() => route.params.token)
const { getAdjuntoUrl } = useEstudios()
const {
  fetchPublicEstudiosDashboard,
  updatePublicEstudioDashboard,
  deletePublicEstudioDashboardAdjunto,
  fetchEstudioAsignacionLink,
  createEstudioAsignacionLink,
  getPublicUrlAsignacion,
  fetchPublicVoluntarios,
  createPublicVoluntario,
  updatePublicVoluntario,
  deletePublicVoluntario,
} = useEstudioDashboardTokens()
const { reverseGeocode } = useNominatim()
const { resolveLocation } = useGoogleMapsUrl()

const loading = ref(true)
const errorMsg = ref(null)
const congregacionNombre = ref('')
const estudios = ref([])
const voluntarios = ref([])

// vista: 'lista' | 'form'. Este enlace sólo permite ver/editar los estudios ya
// seleccionados por la congregación: no hay alta de estudios nuevos.
const view = ref('lista')
const editingId = ref(null)

const emptyForm = () => ({
  publicador: '',
  whatsapp: '',
  estudiante: '',
  estudianteWhatsapp: '',
  direccion: '',
  lat: null,
  lng: null,
  fechaInicio: '',
  asignadoA: '',
  asignadoAWhatsapp: '',
  aQuienSePaso: '',
  observaciones: '',
})
const form = ref(emptyForm())
const saving = ref(false)
const error = ref(null)
const success = ref(false)

// ─── Adjuntos (fotos y documentos) ───────────────────────────────────────────
const MAX_ADJUNTOS = 6
const MAX_ADJUNTO_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPT_ADJUNTOS = 'image/*,.pdf,.doc,.docx,.xls,.xlsx'
// Adjuntos ya guardados del estudio que se está editando (se borran al toque)
const adjuntosExistentes = ref([])
// Archivos elegidos en esta sesión, se suben recién al guardar el formulario
const pendingFiles = ref([])
const adjuntoError = ref(null)
const eliminandoAdjuntoId = ref(null)

const isImagen = (mimeType) => (mimeType || '').startsWith('image/')

const formatBytes = (bytes) => {
  if (!bytes) return ''
  const kb = bytes / 1024
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`
}

const onFilesSelected = (e) => {
  const nuevos = Array.from(e.target.files || [])
  e.target.value = ''
  adjuntoError.value = null
  const totalActual = adjuntosExistentes.value.length + pendingFiles.value.length
  const disponibles = MAX_ADJUNTOS - totalActual
  if (disponibles <= 0) {
    adjuntoError.value = `Como máximo se pueden adjuntar ${MAX_ADJUNTOS} archivos.`
    return
  }
  const aceptados = []
  for (const file of nuevos) {
    if (file.size > MAX_ADJUNTO_SIZE) {
      adjuntoError.value = `"${file.name}" pesa demasiado (máximo 10MB).`
      continue
    }
    if (aceptados.length >= disponibles) {
      adjuntoError.value = `Como máximo se pueden adjuntar ${MAX_ADJUNTOS} archivos.`
      break
    }
    aceptados.push(file)
  }
  pendingFiles.value = [...pendingFiles.value, ...aceptados]
}

const quitarPendingFile = (index) => {
  pendingFiles.value = pendingFiles.value.filter((_, i) => i !== index)
  adjuntoError.value = null
}

const eliminarAdjuntoExistente = async (adjunto) => {
  eliminandoAdjuntoId.value = adjunto.id
  try {
    const res = await deletePublicEstudioDashboardAdjunto(token.value, editingId.value, adjunto.id)
    if (!res.error) {
      adjuntosExistentes.value = adjuntosExistentes.value.filter((a) => a.id !== adjunto.id)
    }
  } finally {
    eliminandoAdjuntoId.value = null
  }
}

const load = async () => {
  loading.value = true
  errorMsg.value = null
  if (!token.value) {
    errorMsg.value = 'Enlace inválido: falta el token.'
    loading.value = false
    return
  }
  const res = await fetchPublicEstudiosDashboard(token.value)
  if (res.error) {
    errorMsg.value = 'Este enlace no es válido o fue eliminado.'
  } else {
    congregacionNombre.value = res.congregacion?.name || ''
    estudios.value = res.estudios || []
    document.title = 'Estudios compartidos'
    const resVoluntarios = await fetchPublicVoluntarios(token.value)
    voluntarios.value = resVoluntarios.voluntarios || []
  }
  loading.value = false
}

// ─── Filtros de la lista ─────────────────────────────────────────────────────
// Un estudio "pasado" es uno que el publicador que lo cargó (típicamente de
// campaña) ya no puede seguir atendiendo y lo dejó anotado en "aQuienSePaso".
// Antes vivían en una sección aparte; ahora son un filtro más de la misma lista.
const filtroAsignado = ref('todos') // 'todos' | 'si' | 'no' | 'pasados'
const filtroVoluntario = ref('') // nombre exacto del voluntario asignado, '' = todos
const busqueda = ref('')

const estudiosFiltrados = computed(() => {
  const q = busqueda.value.trim().toLowerCase()
  return estudios.value.filter((e) => {
    if (filtroAsignado.value === 'pasados' && !e.aQuienSePaso) return false
    if (filtroAsignado.value === 'si' && !e.asignadoA) return false
    if (filtroAsignado.value === 'no' && e.asignadoA) return false
    if (filtroVoluntario.value && e.asignadoA !== filtroVoluntario.value) return false
    if (q) {
      const texto = `${e.estudiante || ''} ${e.publicador || ''} ${e.direccion || ''}`.toLowerCase()
      if (!texto.includes(q)) return false
    }
    return true
  })
})

// ─── Mapa para marcar la ubicación del estudio ───────────────────────────────
const showMap = ref(false)
const mapDiv = ref(null)
let leafletMap = null
let mapMarker = null
const tiles = useMapTiles(() => leafletMap, { local: true })

const placeMarker = (latlng) => {
  if (mapMarker) {
    mapMarker.setLatLng(latlng)
  } else {
    mapMarker = L.circleMarker(latlng, { radius: 9, weight: 3, fillOpacity: 0.6 }).addTo(leafletMap)
  }
}

const destroyMap = () => {
  leafletMap?.remove()
  leafletMap = null
  mapMarker = null
  showMap.value = false
}

const toggleMap = async () => {
  if (showMap.value) {
    destroyMap()
    return
  }
  showMap.value = true
  await nextTick()
  const hasCoords = form.value.lat != null && form.value.lng != null
  const start = hasCoords ? [form.value.lat, form.value.lng] : DEFAULT_CENTER
  leafletMap = L.map(mapDiv.value).setView(start, hasCoords ? 17 : 14)
  tiles.applyCurrentTiles(leafletMap)
  if (hasCoords) placeMarker({ lat: form.value.lat, lng: form.value.lng })
  leafletMap.on('click', async (e) => {
    form.value.lat = e.latlng.lat
    form.value.lng = e.latlng.lng
    placeMarker(e.latlng)
    try {
      const res = await reverseGeocode({ lat: e.latlng.lat, lng: e.latlng.lng })
      if (res?.display_name && !form.value.direccion) form.value.direccion = res.display_name
    } catch {
      // Sin geocodificación: las coordenadas ya quedaron marcadas igual.
    }
  })
  setTimeout(() => leafletMap?.invalidateSize(), 100)
}

// ─── Pegar link de Google Maps (alternativa a marcar en el mapa) ────────────
const linkTexto = ref('')
const linkError = ref(null)
const linkResolving = ref(false)

const usarLink = async () => {
  const texto = linkTexto.value.trim()
  if (!texto) return
  linkError.value = null
  linkResolving.value = true
  try {
    const loc = await resolveLocation(texto)
    if (!loc) {
      linkError.value = 'No pudimos reconocer una ubicación en ese texto.'
      return
    }
    form.value.lat = loc.lat
    form.value.lng = loc.lng
    if (showMap.value && leafletMap) {
      placeMarker(loc)
      leafletMap.setView([loc.lat, loc.lng], 17)
    } else {
      await toggleMap()
    }
    if (!form.value.direccion) {
      try {
        const res = await reverseGeocode(loc)
        if (res?.display_name) form.value.direccion = res.display_name
      } catch {
        // Sin geocodificación: las coordenadas ya quedaron marcadas igual.
      }
    }
  } finally {
    linkResolving.value = false
  }
}

const editarEstudio = (estudio) => {
  editingId.value = estudio.id
  form.value = {
    publicador: estudio.publicador,
    whatsapp: estudio.whatsapp,
    estudiante: estudio.estudiante,
    estudianteWhatsapp: estudio.estudianteWhatsapp,
    direccion: estudio.direccion,
    lat: estudio.lat,
    lng: estudio.lng,
    fechaInicio: estudio.fechaInicio,
    asignadoA: estudio.asignadoA,
    asignadoAWhatsapp: estudio.asignadoAWhatsapp,
    aQuienSePaso: estudio.aQuienSePaso,
    observaciones: estudio.observaciones,
  }
  error.value = null
  success.value = false
  adjuntosExistentes.value = estudio.adjuntos || []
  pendingFiles.value = []
  adjuntoError.value = null
  linkTexto.value = ''
  linkError.value = null
  voluntarioSeleccionado.value = ''
  view.value = 'form'
}

const submit = async () => {
  if (!form.value.publicador.trim() && !form.value.estudiante.trim()) {
    error.value = 'Ingresá al menos el publicador o el estudiante.'
    return
  }
  saving.value = true
  error.value = null
  const res = await updatePublicEstudioDashboard(token.value, editingId.value, form.value, pendingFiles.value)
  saving.value = false
  if (res.error) {
    error.value = res.error
  } else {
    destroyMap()
    pendingFiles.value = []
    success.value = true
    await load()
  }
}

const volverALista = () => {
  destroyMap()
  view.value = 'lista'
  success.value = false
  editingId.value = null
  form.value = emptyForm()
  adjuntosExistentes.value = []
  pendingFiles.value = []
  adjuntoError.value = null
  voluntarioSeleccionado.value = ''
}

// ─── Compartir el enlace individual del estudio con quien lo va a atender ───
const showShareDialog = ref(false)
const shareEstudio = ref(null)
const shareLoading = ref(false)
const shareUrl = ref('')
const shareCopied = ref(false)

const abrirCompartir = async (estudio) => {
  shareEstudio.value = estudio
  showShareDialog.value = true
  shareCopied.value = false
  shareUrl.value = ''
  shareLoading.value = true
  try {
    let result = await fetchEstudioAsignacionLink(token.value, estudio.id)
    if (!result.token) result = await createEstudioAsignacionLink(token.value, estudio.id)
    shareUrl.value = result.token ? getPublicUrlAsignacion(result.token) : ''
  } finally {
    shareLoading.value = false
  }
}

const cerrarCompartir = () => {
  showShareDialog.value = false
  shareEstudio.value = null
}

const copyShareUrl = () => {
  navigator.clipboard.writeText(shareUrl.value)
  shareCopied.value = true
  setTimeout(() => { shareCopied.value = false }, 2000)
}

const sendShareWhatsapp = () => {
  const text = encodeURIComponent(shareUrl.value)
  window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank', 'noopener')
}

// ─── Lista de voluntarios para asignar estudios ─────────────────────────────
const showVoluntariosDialog = ref(false)
const voluntariosOrdenados = computed(() =>
  voluntarios.value.slice().sort((a, b) => a.nombre.localeCompare(b.nombre))
)
const voluntarioNombre = ref('')
const voluntarioWhatsapp = ref('')
const editingVoluntarioId = ref(null)
const voluntarioSaving = ref(false)
const voluntarioError = ref(null)
const eliminandoVoluntarioId = ref(null)

const abrirVoluntarios = () => {
  showVoluntariosDialog.value = true
  cancelarEdicionVoluntario()
}

const cerrarVoluntarios = () => {
  showVoluntariosDialog.value = false
  cancelarEdicionVoluntario()
}

const cancelarEdicionVoluntario = () => {
  editingVoluntarioId.value = null
  voluntarioNombre.value = ''
  voluntarioWhatsapp.value = ''
  voluntarioError.value = null
}

const editarVoluntario = (v) => {
  editingVoluntarioId.value = v.id
  voluntarioNombre.value = v.nombre
  voluntarioWhatsapp.value = v.whatsapp
  voluntarioError.value = null
}

const guardarVoluntario = async () => {
  const nombre = voluntarioNombre.value.trim()
  if (!nombre) {
    voluntarioError.value = 'Ingresá el nombre del voluntario.'
    return
  }
  voluntarioSaving.value = true
  voluntarioError.value = null
  const payload = { nombre, whatsapp: voluntarioWhatsapp.value.trim() }
  const res = editingVoluntarioId.value
    ? await updatePublicVoluntario(token.value, editingVoluntarioId.value, payload)
    : await createPublicVoluntario(token.value, payload)
  voluntarioSaving.value = false
  if (res.error) {
    voluntarioError.value = res.error
    return
  }
  if (editingVoluntarioId.value) {
    const v = voluntarios.value.find((x) => x.id === editingVoluntarioId.value)
    if (v) { v.nombre = nombre; v.whatsapp = payload.whatsapp }
  } else {
    voluntarios.value.push({ id: res.id, nombre: res.nombre, whatsapp: res.whatsapp })
  }
  cancelarEdicionVoluntario()
}

const eliminarVoluntario = async (v) => {
  eliminandoVoluntarioId.value = v.id
  try {
    const res = await deletePublicVoluntario(token.value, v.id)
    if (!res.error) {
      voluntarios.value = voluntarios.value.filter((x) => x.id !== v.id)
      if (editingVoluntarioId.value === v.id) cancelarEdicionVoluntario()
    }
  } finally {
    eliminandoVoluntarioId.value = null
  }
}

// Al elegir un voluntario de la lista, autocompleta nombre y whatsapp del asignado.
const voluntarioSeleccionado = ref('')
const elegirVoluntario = () => {
  const v = voluntarios.value.find((x) => x.id === voluntarioSeleccionado.value)
  if (v) {
    form.value.asignadoA = v.nombre
    form.value.asignadoAWhatsapp = v.whatsapp
  }
}

const formatFecha = (f) => {
  if (!f) return ''
  const [y, m, d] = f.split('-')
  return y && m && d ? `${d}/${m}/${y}` : f
}

onMounted(load)
</script>

<template>
  <div class="simple-page">
    <button
      type="button"
      class="theme-toggle-btn"
      :title="themeStore.theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
      @click="themeStore.toggle()"
    >
      <i :class="themeStore.theme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" />
    </button>

    <img src="/territorios-icono.png" alt="Logo" class="simple-logo">

    <div v-if="loading" class="simple-loading">
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div v-else-if="errorMsg" class="simple-error">{{ errorMsg }}</div>

    <template v-else>
      <h1 class="simple-title">Estudios compartidos{{ congregacionNombre ? ` — ${congregacionNombre}` : '' }}</h1>

      <!-- Lista de estudios seleccionados -->
      <template v-if="view === 'lista'">
        <div v-if="!estudios.length" class="simple-hint">
          Este enlace todavía no tiene estudios compartidos.
        </div>

        <template v-else>
          <div class="simple-buscador">
            <i class="pi pi-search" />
            <input
              v-model="busqueda"
              type="text"
              class="simple-input simple-buscador-input"
              placeholder="Buscar por estudiante, publicador o dirección..."
            >
          </div>

          <div class="simple-filtros">
            <button type="button" class="simple-filtro-btn simple-voluntarios-btn" @click="abrirVoluntarios">
              <i class="pi pi-users" /> Voluntarios ({{ voluntarios.length }})
            </button>
            <button
              type="button"
              class="simple-filtro-btn"
              :class="{ 'simple-filtro-btn-active': filtroAsignado === 'todos' }"
              @click="filtroAsignado = 'todos'"
            >Todos ({{ estudios.length }})</button>
            <button
              type="button"
              class="simple-filtro-btn"
              :class="{ 'simple-filtro-btn-active': filtroAsignado === 'no' }"
              @click="filtroAsignado = 'no'"
            >Sin asignar ({{ estudios.filter(e => !e.asignadoA).length }})</button>
            <button
              type="button"
              class="simple-filtro-btn"
              :class="{ 'simple-filtro-btn-active': filtroAsignado === 'si' }"
              @click="filtroAsignado = 'si'"
            >Asignados ({{ estudios.filter(e => e.asignadoA).length }})</button>
            <button
              v-if="estudios.some(e => e.aQuienSePaso)"
              type="button"
              class="simple-filtro-btn"
              :class="{ 'simple-filtro-btn-active': filtroAsignado === 'pasados' }"
              @click="filtroAsignado = 'pasados'"
            >Pasados ({{ estudios.filter(e => e.aQuienSePaso).length }})</button>
          </div>

          <div v-if="voluntariosOrdenados.length" class="simple-field simple-filtro-voluntario">
            <label class="simple-field-label" for="simple-filtro-voluntario-select">Filtrar por voluntario asignado</label>
            <select
              id="simple-filtro-voluntario-select"
              v-model="filtroVoluntario"
              class="simple-input simple-select"
            >
              <option value="">— Todos —</option>
              <option v-for="v in voluntariosOrdenados" :key="v.id" :value="v.nombre">{{ v.nombre }}</option>
            </select>
          </div>

          <div v-if="!estudiosFiltrados.length" class="simple-hint">
            No hay estudios que coincidan con este filtro.
          </div>

          <div v-else class="simple-list">
            <div v-for="e in estudiosFiltrados" :key="e.id" class="simple-item" :class="{ 'simple-item-pasado': e.aQuienSePaso }">
              <div class="simple-item-main">
                <div class="simple-item-title-row">
                  <div class="simple-item-title">{{ e.estudiante || 'Sin nombre' }}</div>
                  <span v-if="e.aQuienSePaso" class="simple-badge simple-badge-pasado">Pasado</span>
                  <span v-if="e.asignadoA" class="simple-badge simple-badge-ok">Asignado</span>
                  <span v-else class="simple-badge simple-badge-warn">Sin asignar</span>
                </div>
                <div v-if="e.estudianteWhatsapp" class="simple-item-row">
                  <a :href="`https://wa.me/${String(e.estudianteWhatsapp).replace(/\D/g, '')}`" target="_blank" rel="noopener">
                    <i class="pi pi-whatsapp" /> {{ e.estudianteWhatsapp }}
                  </a>
                </div>
                <div v-if="e.publicador" class="simple-item-row">
                  {{ e.aQuienSePaso ? 'Publicador que lo cargó' : 'Publicador' }}: {{ e.publicador }}
                  <a v-if="e.whatsapp" :href="`https://wa.me/${String(e.whatsapp).replace(/\D/g, '')}`" target="_blank" rel="noopener">
                    <i class="pi pi-whatsapp" /> {{ e.whatsapp }}
                  </a>
                </div>
                <div v-if="e.direccion" class="simple-item-row">
                  {{ e.direccion }}
                  <a
                    v-if="e.lat && e.lng"
                    :href="`https://www.google.com/maps?q=${e.lat},${e.lng}`"
                    target="_blank"
                    rel="noopener"
                  >ver mapa</a>
                </div>
                <div v-if="e.fechaInicio" class="simple-item-row">Inicio: {{ formatFecha(e.fechaInicio) }}</div>
                <div v-if="e.aQuienSePaso" class="simple-item-row simple-item-pasado-a">Se lo pasó a: {{ e.aQuienSePaso }}</div>
                <div v-if="e.asignadoA" class="simple-item-row">
                  Asignado a: {{ e.asignadoA }}
                  <a v-if="e.asignadoAWhatsapp" :href="`https://wa.me/${String(e.asignadoAWhatsapp).replace(/\D/g, '')}`" target="_blank" rel="noopener">
                    <i class="pi pi-whatsapp" /> {{ e.asignadoAWhatsapp }}
                  </a>
                </div>
                <div v-if="e.observaciones" class="simple-item-row simple-item-obs">{{ e.observaciones }}</div>
                <div v-if="e.adjuntos?.length" class="simple-item-adjuntos">
                  <a
                    v-for="a in e.adjuntos"
                    :key="a.id"
                    :href="getAdjuntoUrl(a.id)"
                    target="_blank"
                    rel="noopener"
                    class="simple-item-adjunto"
                    :title="a.originalName"
                  >
                    <img v-if="isImagen(a.mimeType)" :src="getAdjuntoUrl(a.id)" alt="">
                    <i v-else class="pi pi-file" />
                  </a>
                </div>
              </div>
              <div class="simple-item-actions">
                <button class="simple-edit-btn" @click="editarEstudio(e)">
                  <i class="pi pi-pencil" /> Editar
                </button>
                <button class="simple-edit-btn simple-share-btn" @click="abrirCompartir(e)">
                  <i class="pi pi-share-alt" /> Compartir
                </button>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- Formulario de edición -->
      <template v-else>
        <div v-if="success" class="simple-done">
          <i class="pi pi-check-circle" />
          <div>¡Listo! El estudio fue actualizado.</div>
          <button class="simple-btn simple-btn-secondary" @click="volverALista">Volver a la lista</button>
        </div>

        <form v-else class="simple-form" @submit.prevent="submit">
          <button type="button" class="simple-back simple-field-full" @click="volverALista">
            <i class="pi pi-arrow-left" /> Volver a la lista
          </button>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-publicador">Tu Nombre</label>
            <input
              id="simple-publicador"
              v-model="form.publicador"
              type="text"
              class="simple-input"
              placeholder="Tu Nombre"
            >
          </div>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-whatsapp">Tu Whatsapp <span class="simple-optional">(opcional)</span></label>
            <div class="simple-help">Para poder contactarte si surge alguna duda con el estudio.</div>
            <input
              id="simple-whatsapp"
              v-model="form.whatsapp"
              type="tel"
              class="simple-input"
              placeholder="Tu Whatsapp"
            >
          </div>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-estudiante">Estudiante</label>
            <input
              id="simple-estudiante"
              v-model="form.estudiante"
              type="text"
              class="simple-input"
              placeholder="Nombre del estudiante"
            >
          </div>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-estudiante-whatsapp">Teléfono del estudiante <span class="simple-optional">(opcional)</span></label>
            <input
              id="simple-estudiante-whatsapp"
              v-model="form.estudianteWhatsapp"
              type="tel"
              class="simple-input"
              placeholder="+54 9 264 555 5555"
            >
          </div>

          <div class="simple-gps-block simple-field-full">
            <div v-if="form.lat && form.lng" class="simple-gps-status">
              <i class="pi pi-check-circle" />
              Ubicación capturada —
              <a :href="`https://www.google.com/maps?q=${form.lat},${form.lng}`" target="_blank" rel="noopener">ver en el mapa</a>
            </div>

            <label class="simple-field-label" for="simple-maps-link">Pegar link de Google Maps <span class="simple-optional">(opcional)</span></label>
            <div class="simple-help">Compartí la ubicación desde Google Maps ("Compartir" → "Copiar enlace") y pegala acá.</div>
            <div class="simple-link-row">
              <input
                id="simple-maps-link"
                v-model="linkTexto"
                type="text"
                class="simple-input"
                placeholder="https://maps.app.goo.gl/..."
                @keyup.enter="usarLink"
              >
              <button type="button" class="simple-link-btn" :disabled="linkResolving || !linkTexto.trim()" @click="usarLink">
                <i class="pi" :class="linkResolving ? 'pi-spin pi-spinner' : 'pi-check'" />
              </button>
            </div>
            <div v-if="linkError" class="simple-error-msg">{{ linkError }}</div>

            <button type="button" class="simple-gps-btn simple-map-btn" @click="toggleMap">
              <i class="pi" :class="showMap ? 'pi-times' : 'pi-map'" />
              {{ showMap ? 'Ocultar el mapa' : 'Ubicar en el mapa' }}
            </button>
            <div v-show="showMap" ref="mapDiv" class="simple-map" />
            <div v-if="showMap" class="simple-help">Tocá el mapa para marcar dónde se realiza el estudio.</div>
          </div>

          <div class="simple-field simple-field-full">
            <label class="simple-field-label" for="simple-direccion">Dirección o referencia <span class="simple-optional">(opcional)</span></label>
            <input
              id="simple-direccion"
              v-model="form.direccion"
              type="text"
              class="simple-input"
              placeholder="Dónde se realiza el estudio"
            >
          </div>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-fecha">Fecha de inicio <span class="simple-optional">(opcional)</span></label>
            <input
              id="simple-fecha"
              v-model="form.fechaInicio"
              type="date"
              class="simple-input"
            >
          </div>

          <div class="simple-field simple-field-full">
            <label class="simple-field-label" for="simple-voluntario-select">Elegir voluntario de la lista <span class="simple-optional">(opcional)</span></label>
            <div class="simple-link-row">
              <select
                id="simple-voluntario-select"
                v-model="voluntarioSeleccionado"
                class="simple-input simple-select"
                @change="elegirVoluntario"
              >
                <option value="">— Escribir manualmente —</option>
                <option v-for="v in voluntariosOrdenados" :key="v.id" :value="v.id">{{ v.nombre }}</option>
              </select>
              <button type="button" class="simple-link-btn" title="Administrar lista de voluntarios" @click="abrirVoluntarios">
                <i class="pi pi-users" />
              </button>
            </div>
          </div>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-asignado">Asignado a <span class="simple-optional">(opcional)</span></label>
            <div class="simple-help">Quién le va a hacer el seguimiento a este estudio (visitas, avances).</div>
            <input
              id="simple-asignado"
              v-model="form.asignadoA"
              type="text"
              class="simple-input"
              placeholder="Nombre de quien se hace cargo"
            >
          </div>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-asignado-whatsapp">WhatsApp del asignado <span class="simple-optional">(opcional)</span></label>
            <input
              id="simple-asignado-whatsapp"
              v-model="form.asignadoAWhatsapp"
              type="tel"
              class="simple-input"
              placeholder="+54 9 264 555 5555"
            >
          </div>

          <div class="simple-field">
            <label class="simple-field-label" for="simple-paso">A quién se lo pasó <span class="simple-optional">(opcional)</span></label>
            <input
              id="simple-paso"
              v-model="form.aQuienSePaso"
              type="text"
              class="simple-input"
              placeholder="Si el estudio se pasó a otro publicador"
            >
          </div>

          <div class="simple-field simple-field-full">
            <label class="simple-field-label" for="simple-obs">Observaciones <span class="simple-optional">(opcional)</span></label>
            <textarea
              id="simple-obs"
              v-model="form.observaciones"
              class="simple-input simple-textarea"
              rows="3"
              placeholder="Horarios, avances, detalles del estudio..."
            />
          </div>

          <div class="simple-field simple-field-full">
            <label class="simple-field-label">Fotos o documentos <span class="simple-optional">(opcional)</span></label>
            <div class="simple-help">Podés adjuntar hasta {{ MAX_ADJUNTOS }} archivos (imágenes o PDF/Word/Excel, máx. 10MB c/u).</div>

            <div v-if="adjuntosExistentes.length" class="simple-adjuntos-list">
              <div v-for="a in adjuntosExistentes" :key="a.id" class="simple-adjunto-item">
                <a :href="getAdjuntoUrl(a.id)" target="_blank" rel="noopener" class="simple-adjunto-link">
                  <img v-if="isImagen(a.mimeType)" :src="getAdjuntoUrl(a.id)" class="simple-adjunto-thumb" alt="">
                  <i v-else class="pi pi-file simple-adjunto-icon" />
                  <span class="simple-adjunto-name">{{ a.originalName }}</span>
                </a>
                <button
                  type="button"
                  class="simple-adjunto-remove"
                  :disabled="eliminandoAdjuntoId === a.id"
                  @click="eliminarAdjuntoExistente(a)"
                >
                  <i class="pi" :class="eliminandoAdjuntoId === a.id ? 'pi-spin pi-spinner' : 'pi-times'" />
                </button>
              </div>
            </div>

            <div v-if="pendingFiles.length" class="simple-adjuntos-list">
              <div v-for="(f, i) in pendingFiles" :key="`${f.name}-${i}`" class="simple-adjunto-item">
                <div class="simple-adjunto-link">
                  <i class="pi" :class="f.type.startsWith('image/') ? 'pi-image' : 'pi-file'" />
                  <span class="simple-adjunto-name">{{ f.name }} <span class="simple-optional">({{ formatBytes(f.size) }})</span></span>
                </div>
                <button type="button" class="simple-adjunto-remove" @click="quitarPendingFile(i)">
                  <i class="pi pi-times" />
                </button>
              </div>
            </div>

            <label
              v-if="adjuntosExistentes.length + pendingFiles.length < MAX_ADJUNTOS"
              class="simple-file-btn"
            >
              <i class="pi pi-paperclip" /> Adjuntar archivos
              <input type="file" multiple :accept="ACCEPT_ADJUNTOS" class="simple-file-input" @change="onFilesSelected">
            </label>

            <div v-if="adjuntoError" class="simple-error-msg">{{ adjuntoError }}</div>
          </div>

          <div v-if="error" class="simple-error-msg simple-field-full">{{ error }}</div>

          <button type="submit" class="simple-btn simple-field-full" :disabled="saving">
            {{ saving ? 'Enviando...' : 'Guardar cambios' }}
          </button>
        </form>
      </template>

      <!-- Modal: compartir el enlace individual con quien va a atender el estudio -->
      <div v-if="showShareDialog" class="simple-modal-overlay" @click.self="cerrarCompartir">
        <div class="simple-modal-card">
          <div class="simple-modal-title">Compartir estudio</div>
          <div class="simple-modal-sub">
            Enviá este enlace a quien va a atender el estudio de
            {{ shareEstudio?.estudiante || shareEstudio?.publicador || 'este estudio' }}.
            Va a poder ver los datos, indicar quién se hace cargo y agregar notas de seguimiento.
          </div>
          <div v-if="shareLoading" class="simple-modal-loading"><i class="pi pi-spin pi-spinner" /></div>
          <template v-else>
            <div class="simple-link-row">
              <input type="text" class="simple-input" :value="shareUrl" readonly>
              <button type="button" class="simple-link-btn" @click="copyShareUrl">
                <i class="pi" :class="shareCopied ? 'pi-check' : 'pi-copy'" />
              </button>
            </div>
            <button type="button" class="simple-btn" @click="sendShareWhatsapp">
              <i class="pi pi-whatsapp" /> Enviar por WhatsApp
            </button>
          </template>
          <button type="button" class="simple-btn simple-btn-secondary" @click="cerrarCompartir">Cerrar</button>
        </div>
      </div>

      <!-- Modal: gestión de la lista de voluntarios para asignar estudios -->
      <div v-if="showVoluntariosDialog" class="simple-modal-overlay" @click.self="cerrarVoluntarios">
        <div class="simple-modal-card">
          <div class="simple-modal-title">Voluntarios</div>
          <div class="simple-modal-sub">
            Mantené acá la lista de voluntarios que reciben estudios, con su WhatsApp,
            para asignarlos más rápido desde cada estudio.
          </div>

          <div v-if="voluntariosOrdenados.length" class="simple-adjuntos-list">
            <div v-for="v in voluntariosOrdenados" :key="v.id" class="simple-adjunto-item">
              <div class="simple-adjunto-link">
                <i class="pi pi-user simple-adjunto-icon" />
                <span class="simple-adjunto-name">
                  {{ v.nombre }}
                  <span v-if="v.whatsapp" class="simple-optional">— {{ v.whatsapp }}</span>
                </span>
              </div>
              <div class="simple-voluntario-actions">
                <button type="button" class="simple-adjunto-remove simple-voluntario-edit" @click="editarVoluntario(v)">
                  <i class="pi pi-pencil" />
                </button>
                <button
                  type="button"
                  class="simple-adjunto-remove"
                  :disabled="eliminandoVoluntarioId === v.id"
                  @click="eliminarVoluntario(v)"
                >
                  <i class="pi" :class="eliminandoVoluntarioId === v.id ? 'pi-spin pi-spinner' : 'pi-times'" />
                </button>
              </div>
            </div>
          </div>
          <div v-else class="simple-hint simple-voluntarios-empty">Todavía no hay voluntarios cargados.</div>

          <div class="simple-voluntario-form">
            <label class="simple-field-label">{{ editingVoluntarioId ? 'Editar voluntario' : 'Agregar voluntario' }}</label>
            <input
              v-model="voluntarioNombre"
              type="text"
              class="simple-input"
              placeholder="Nombre"
            >
            <input
              v-model="voluntarioWhatsapp"
              type="tel"
              class="simple-input"
              placeholder="WhatsApp (opcional)"
            >
            <div v-if="voluntarioError" class="simple-error-msg">{{ voluntarioError }}</div>
            <div class="simple-voluntario-form-actions">
              <button
                v-if="editingVoluntarioId"
                type="button"
                class="simple-btn simple-btn-secondary"
                @click="cancelarEdicionVoluntario"
              >Cancelar</button>
              <button type="button" class="simple-btn" :disabled="voluntarioSaving" @click="guardarVoluntario">
                {{ voluntarioSaving ? 'Guardando...' : (editingVoluntarioId ? 'Guardar cambios' : 'Agregar') }}
              </button>
            </div>
          </div>

          <button type="button" class="simple-btn simple-btn-secondary" @click="cerrarVoluntarios">Cerrar</button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.simple-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px clamp(16px, 4vw, 48px) 48px;
  background: var(--bg-grouped, #f2f2f7);
  text-align: center;
}

.theme-toggle-btn {
  position: fixed;
  top: max(env(safe-area-inset-top, 0px), 16px);
  right: 16px;
  z-index: 10;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid var(--separator, rgba(0, 0, 0, 0.15));
  background: var(--bg-primary, #fff);
  color: var(--label-secondary, #666);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.theme-toggle-btn:active {
  opacity: 0.7;
}

.simple-logo {
  width: 112px;
  height: 112px;
  margin-bottom: 28px;
}

.simple-loading {
  font-size: 2rem;
  color: var(--accent, #007aff);
  margin-top: 40px;
}

.simple-error {
  font-size: 18px;
  color: var(--label-secondary, #666);
  margin-top: 20px;
  max-width: 420px;
}

.simple-title {
  width: 100%;
  max-width: 1100px;
  font-size: 26px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 20px;
}

.simple-hint {
  width: 100%;
  max-width: 1100px;
  background: var(--info-soft, #e6f2ff);
  color: var(--label-primary, #333);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 16px;
  text-align: left;
}

.simple-buscador {
  position: relative;
  width: 100%;
  max-width: 1100px;
  margin-top: 16px;
  display: flex;
  align-items: center;
}

.simple-buscador .pi-search {
  position: absolute;
  left: 14px;
  color: var(--label-tertiary, #999);
  font-size: 14px;
  pointer-events: none;
}

.simple-buscador-input {
  padding-left: 38px;
}

.simple-filtro-voluntario {
  width: 100%;
  max-width: 1100px;
  margin-top: 12px;
  text-align: left;
}

.simple-filtros {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  max-width: 1100px;
  margin-top: 16px;
}

@media (min-width: 680px) {
  .simple-filtros {
    justify-content: flex-start;
  }
}

.simple-filtro-btn {
  border: 1.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 20px;
  background: var(--bg-primary, #fff);
  color: var(--label-secondary, #555);
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.simple-filtro-btn-active {
  border-color: var(--accent, #007aff);
  background: var(--accent, #007aff);
  color: #fff;
}

.simple-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  max-width: 1100px;
  margin-top: 16px;
}

@media (min-width: 680px) {
  .simple-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1100px) {
  .simple-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

.simple-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--bg-primary, #fff);
  border-radius: 14px;
  padding: 14px 16px;
  text-align: left;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.simple-item-pasado {
  opacity: 0.9;
}

.simple-item-pasado-a {
  font-weight: 600;
  color: var(--label-primary, #333);
}

.simple-item-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.simple-item-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--label-primary, #111);
}

.simple-badge {
  flex-shrink: 0;
  border-radius: 8px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.simple-badge-ok {
  background: var(--success-soft, #e3f7e8);
  color: var(--success-deep, #1e7d34);
}

.simple-badge-warn {
  background: rgba(255, 149, 0, 0.15);
  color: #b25f00;
}

.simple-badge-pasado {
  background: rgba(142, 142, 147, 0.18);
  color: var(--label-secondary, #636366);
}

.simple-item-row {
  font-size: 14px;
  color: var(--label-secondary, #555);
  line-height: 1.4;
}

.simple-item-row a {
  color: var(--accent, #007aff);
  margin-left: 4px;
}

.simple-item-obs {
  font-style: italic;
  margin-top: 4px;
}

.simple-item-actions {
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 4px;
}

.simple-edit-btn {
  flex: 1;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: rgba(0, 122, 255, 0.1);
  color: var(--accent, #007aff);
  font-size: 14px;
  font-weight: 600;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.simple-share-btn {
  background: var(--success-soft, #e3f7e8);
  color: var(--success-deep, #1e7d34);
}

.simple-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-top: max(env(safe-area-inset-top, 0px), 20px);
  padding-bottom: max(env(safe-area-inset-bottom, 0px), 20px);
  overflow-y: auto;
  z-index: 20;
}

.simple-modal-card {
  width: 100%;
  max-width: 420px;
  max-height: 100%;
  background: var(--bg-primary, #fff);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  margin: auto;
}

.simple-modal-title {
  font-size: 19px;
  font-weight: 700;
  color: var(--label-primary, #111);
}

.simple-modal-sub {
  font-size: 14px;
  color: var(--label-secondary, #666);
  line-height: 1.4;
}

.simple-modal-loading {
  font-size: 1.6rem;
  color: var(--accent, #007aff);
  text-align: center;
  padding: 12px 0;
}

.simple-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px;
  border-radius: 16px;
  background: var(--success-soft, #e3f7e8);
  color: var(--success-deep, #1e7d34);
  font-size: 18px;
  font-weight: 600;
  max-width: 420px;
}

.simple-done .pi {
  font-size: 2.4rem;
}

.simple-form {
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  width: 100%;
  max-width: 420px;
  text-align: left;
}

@media (min-width: 680px) {
  .simple-form {
    max-width: 760px;
    grid-template-columns: 1fr 1fr;
    column-gap: 24px;
  }
}

.simple-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.simple-field-full {
  grid-column: 1 / -1;
}

.simple-back {
  align-self: flex-start;
  border: none;
  background: none;
  color: var(--accent, #007aff);
  font-size: 15px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  -webkit-tap-highlight-color: transparent;
}

.simple-field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 8px;
}

.simple-input {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  padding: 14px 16px;
  font-size: 17px;
  color: var(--label-primary, #111);
}

.simple-textarea {
  resize: vertical;
  font-family: inherit;
}

.simple-error-msg {
  margin-top: 16px;
  color: var(--danger, #ff3b30);
  font-size: 15px;
  text-align: center;
}

.simple-gps-btn {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 14px;
  background: var(--accent, #007aff);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-gps-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.simple-map-btn {
  margin-top: 10px;
  background: rgba(0, 122, 255, 0.12);
  color: var(--accent, #007aff);
}

.simple-map {
  width: 100%;
  height: 280px;
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
}

.simple-optional {
  font-weight: 400;
  color: var(--label-tertiary, #999);
}

.simple-help {
  font-size: 13px;
  color: var(--label-secondary, #777);
  margin-bottom: 8px;
  line-height: 1.4;
}

.simple-gps-status {
  margin-top: 10px;
  font-size: 14px;
  color: var(--success-deep, #1e7d34);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.simple-gps-status a {
  color: inherit;
  text-decoration: underline;
}

.simple-link-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.simple-link-row .simple-input {
  flex: 1;
}

.simple-link-btn {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: rgba(0, 122, 255, 0.12);
  color: var(--accent, #007aff);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.simple-link-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.simple-btn {
  margin-top: 28px;
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 14px;
  background: var(--success, #34c759);
  color: #fff;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.simple-btn-secondary {
  margin-top: 4px;
  background: rgba(0, 0, 0, 0.12);
  color: var(--label-primary, #111);
}

.simple-adjuntos-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.simple-adjunto-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--bg-primary, #fff);
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 8px 10px;
}

.simple-adjunto-link {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: var(--label-primary, #111);
  text-decoration: none;
}

.simple-adjunto-thumb {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.simple-adjunto-icon {
  font-size: 20px;
  color: var(--label-secondary, #666);
  flex-shrink: 0;
}

.simple-adjunto-name {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.simple-adjunto-remove {
  flex-shrink: 0;
  border: none;
  background: rgba(255, 59, 48, 0.1);
  color: var(--danger, #ff3b30);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.simple-adjunto-remove:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.simple-file-btn {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 12px;
  border: 1.5px dashed rgba(0, 0, 0, 0.25);
  color: var(--accent, #007aff);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.simple-file-input {
  display: none;
}

.simple-item-adjuntos {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.simple-item-adjuntos a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-grouped, #f2f2f7);
  color: var(--label-secondary, #666);
}

.simple-item-adjuntos img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.simple-voluntarios-btn {
  display: flex;
  align-items: center;
  gap: 6px;
}

.simple-select {
  appearance: auto;
  cursor: pointer;
}

.simple-voluntario-actions {
  flex-shrink: 0;
  display: flex;
  gap: 6px;
}

.simple-voluntario-edit {
  background: rgba(0, 122, 255, 0.1);
  color: var(--accent, #007aff);
}

.simple-voluntarios-empty {
  margin-top: 8px;
}

.simple-voluntario-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--separator, rgba(0, 0, 0, 0.1));
}

.simple-voluntario-form-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.simple-voluntario-form-actions .simple-btn {
  margin-top: 0;
  width: auto;
  flex: 1;
}
</style>
